'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { Suspense, useEffect, useRef, useState } from 'react';

const AnalyticsContent = () => {
  const pathname = usePathname();
  const isInitialized = useRef(false);
  const searchParams = useSearchParams();
  const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID;
  const ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  const FB_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

  // We do NOT need marketing analytics (GA4, FB, Ads) on internal admin/user pages.
  // This drastically reduces console noise and main-thread blocking on management routes.
  const isProtectedArea = pathname?.includes('/admin') || pathname?.includes('/user');

  // Determine the primary ID (Ads or GA4)
  const primaryId = ADS_ID || GA4_ID;

  const [shouldRenderScripts, setShouldRenderScripts] = useState(false);

  // Initialize and Track
  useEffect(() => {
    // Global delay for script rendering to clear the main thread
    const renderDelay = setTimeout(() => {
      setShouldRenderScripts(true);
    }, 6000);

    return () => clearTimeout(renderDelay);
  }, []);

  useEffect(() => {
    if (isProtectedArea || !primaryId || !shouldRenderScripts) return;

    // Original tracking logic
    const delay = setTimeout(() => {
      if (!window.gtag) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = (...args: unknown[]) => {
          window.dataLayer.push(...args);
        };

        const isDenied = document.cookie.indexOf('cookieConsent=false') > -1;
        const consentState = isDenied ? 'denied' : 'granted';

        window.gtag('consent', 'default', {
          ad_storage: consentState,
          analytics_storage: consentState,
          ad_user_data: consentState,
          ad_personalization: consentState,
          wait_for_update: 500,
        });
      }

      const hasFbq = typeof window.fbq === 'function';

      if (primaryId) {
        if (!isInitialized.current) {
          window.gtag('js', new Date());

          if (GA4_ID) {
            window.gtag('config', GA4_ID, {
              page_path: pathname,
              page_location: window.location.href,
            });
          }

          if (ADS_ID) {
            window.gtag('config', ADS_ID);
          }

          isInitialized.current = true;
        } else {
          if (GA4_ID) {
            window.gtag('config', GA4_ID, {
              page_path: pathname,
              page_location: window.location.href,
            });
          }

          if (FB_ID && hasFbq) {
            window.fbq('track', 'PageView');
          }
        }
      }
    }, 1000); // Small buffer after script render

    return () => clearTimeout(delay);
  }, [
    pathname,
    searchParams,
    FB_ID,
    GA4_ID,
    ADS_ID,
    primaryId,
    isProtectedArea,
    shouldRenderScripts,
  ]);

  if (!primaryId || isProtectedArea) return null;

  return (
    <>
      {shouldRenderScripts && (
        <>
          {/* 
            1. Initialization Script (Inline)
            Sets up dataLayer and default consent immediately when rendered.
          */}
          <Script id="google-analytics-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;

              var isDenied = document.cookie.indexOf('cookieConsent=false') > -1;
              var consentState = isDenied ? 'denied' : 'granted';

              gtag('consent', 'default', {
                  'ad_storage': consentState,
                  'analytics_storage': consentState,
                  'ad_user_data': consentState,
                  'ad_personalization': consentState,
                  'wait_for_update': 500
              });
            `}
          </Script>

          {/* 
            2. Google Tag Script (External)
          */}
          <Script
            id="google-analytics-script"
            strategy="lazyOnload"
            src={`https://www.googletagmanager.com/gtag/js?id=${primaryId}`}
          />

          {/* Meta Pixel */}
          {FB_ID && (
            <Script
              id="fb-pixel"
              strategy="lazyOnload"
              dangerouslySetInnerHTML={{
                __html: `
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${FB_ID}');
                  fbq('track', 'PageView');
                `,
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export function AnalyticsScript() {
  return (
    <Suspense fallback={null}>
      <AnalyticsContent />
    </Suspense>
  );
}
